import { useState } from 'react';

import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import type {
  DocumentTemplate,
  useDocumentTemplates,
} from './useDocumentTemplates';

export type EditingTemplate = {
  id: string;
  name: string;
  content: string;
  kind: DocumentTemplate['kind'];
  sourceFileId: DocumentTemplate['sourceFileId'];
};

export type DeletingTemplate = {
  id: string;
  name: string;
};

type TemplateMutations = ReturnType<typeof useDocumentTemplates>;

export type UseTemplateListStateArgs = {
  deleteTemplate: TemplateMutations['deleteTemplate'];
  refetch: TemplateMutations['refetch'];
};

export const useTemplateListState = ({
  deleteTemplate,
  refetch,
}: UseTemplateListStateArgs) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [editingTemplate, setEditingTemplate] =
    useState<EditingTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] =
    useState<DeletingTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteTemplate({ variables: { id: deletingTemplate.id } });
      toast.success(t('appointmentTools.documents.templateDeleted'));
      setDeletingTemplate(null);
      await refetch();
    } catch {
      toast.error(t('appointmentTools.documents.deleteTemplateFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    editingTemplate,
    setEditingTemplate,
    deletingTemplate,
    setDeletingTemplate,
    isDeleting,
    handleDeleteTemplate,
  };
};
